/**
 * @file EmailSettingsModal.jsx
 * @description Modal de configurações específicas para formato email. Permite selecionar
 * template, configurar imagens e finalizar a criação do conteúdo.
 * 
 * @exports EmailSettingsModal - Componente do modal de configurações de email
 */

import './EmailSettingsModal.css';
import { useState, useEffect } from 'react';
import { Modal } from '../../ui/Modal/Modal';
import { Badge } from '../../ui/Badge/Badge';
import { IoMdArrowBack, IoMdPlay } from 'react-icons/io';
import { LuAlarmClock } from 'react-icons/lu';
import { RiArticleLine } from "react-icons/ri";
import EmailTemplate1 from '../../../imgs/Email_Template_1.png';
import EmailTemplate2 from '../../../imgs/Email_Template_2.png';
import EmailTemplate3 from '../../../imgs/Email_Template_3.png';

const EMAIL_TEMPLATES = [
  {
    id: 'Template Standard MIA',
    name: 'Template Standard MIA',
    description: 'Standard template',
    preview: EmailTemplate1,
    compliance: ['Mexico', 'Brazil', 'No compliance'],
  },
  {
    id: 'Template 1 Onco BR',
    name: 'Template 1 Onco',
    description: 'Onco template',
    preview: EmailTemplate2,
    compliance: ['Brazil'],
  },
  {
    id: 'Template 1 Onco MX',
    name: 'Template 1 Onco',
    description: 'Onco template',
    preview: EmailTemplate3,
    compliance: ['Mexico'],
  },
];

export const EmailSettingsModal = ({ isOpen, onClose, contentData, onBack, initialData, onStartRun }) => {
  const compliance = contentData?.compliance || initialData?.compliance;
  const availableTemplates = EMAIL_TEMPLATES.filter(
    template => template.compliance.includes(compliance)
  );
  
  const [selectedTemplate, setSelectedTemplate] = useState('Template Standard MIA');
  const [useCustomImage, setUseCustomImage] = useState(true);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [isLoadingRun, setIsLoadingRun] = useState(false);

  useEffect(() => {
    if (initialData?.emailSettings) {
      setSelectedTemplate(initialData.emailSettings.template);
      setUseCustomImage(initialData.emailSettings.useCustomImage ?? true);
      setUploadedImage(initialData.emailSettings.image || null);
    } else {
      setSelectedTemplate('Template Standard MIA');
    }
  }, [initialData]);

  const handleImageSelect = (file) => {
    setUploadedImage(file);
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    }
    onClose();
  };

  const handleStartRun = async () => {
    const emailSettings = {
      template: selectedTemplate,
      useCustomImage,
      image: uploadedImage,
    };

    setIsLoadingRun(true);
    try {
      await onStartRun?.(emailSettings);
      onClose();
    } catch (error) {
      console.error('Error starting email run:', error);
    } finally {
      setIsLoadingRun(false);
    }
  };


  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="email-settings-modal__title">
          <button
            type="button"
            className="email-settings-modal__back"
            onClick={handleBack}
            aria-label="Go back"
          >
            <IoMdArrowBack size={20} />
          </button>
          <span>E-mail settings</span>
        </div>
      }
      size="large"
    >
      <div className="email-settings-modal">
        <p className="create-content-modal__mandatory">*Mandatory</p>
        <div className="email-settings-modal__section">
          
          <label className="email-settings-modal__label">
            Select E-mail template <span className="orange-asterisk">*</span>
          </label>
          <div className="email-settings-modal__templates">
            {availableTemplates.map((template) => (
              <div key={template.id} className={template.soon ? "email-template-soon" : "email-template"}>
                <div className="email-template__placeholder">

                <input
                    type="radio"
                    name="template"
                    checked={selectedTemplate === template.id}
                    onChange={() => !template.soon && setSelectedTemplate(template.id)}
                    disabled={template.soon}
                    className="email-template__radio"
                  />
                <span className="email-template__name">{template.name}</span>
                </div>
                <button
                  key={template.id}
                  type="button"
                  onClick={() => !template.soon && setSelectedTemplate(template.id)}
                  disabled={template.soon}
                >
                  {template.soon && (
                      <Badge
                        variant="soon"
                        size="small"
                        className="email-template__badge"
                        icon={<LuAlarmClock />}
                      >
                        Soon
                      </Badge>
                    )}
                  <div className="email-template__preview">
                    {template.preview ? (
                      <img src={template.preview} alt={template.name} />
                    ) : (
                      <div className="email-template__placeholder">
                        <RiArticleLine />

                      </div>
                    )}
                    
                  </div>
                  <div className="email-template__info">
                   
                    <div className="email-template__details">
                      
                      <span className="email-template__description">
                        {template.description}
                      </span>
                    </div>
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* <div className="email-settings-modal__section">
          <label className="email-settings-modal__label">Reference image <span className="orange-asterisk">*</span></label>
          <div className="email-settings-modal__tabs">
            <button
              type="button"
              className={`email-settings-modal__tab ${useCustomImage ? 'email-settings-modal__tab--active' : ''
                }`}
              onClick={() => setUseCustomImage(true)}
            >
              Use custom reference image
            </button>
            <button
              type="button"
              className={`email-settings-modal__tab ${!useCustomImage ? 'email-settings-modal__tab--active' : ''
                }`}
              onClick={() => setUseCustomImage(false)}
            >
              Don't use a reference image
            </button>
          </div>

          {useCustomImage && (
            <div className="email-settings-modal__upload">
              <FileUpload
                onFileSelect={handleImageSelect}
                accept=".jpg,.jpeg,.png"
                maxSize={5 * 1024 * 1024}
                supportedTypes="JPG or PNG"
              />
            </div>
          )}
        </div> */}

        <div className="email-settings-modal__actions">
          <button
            className="button button--gradient-orange button--large button-mid-width"
            onClick={handleStartRun}
            disabled={isLoadingRun}
          >
            <span className="button__icon button__icon--left">
              <IoMdPlay size={20} />
            </span>
            <span className="button__text">{isLoadingRun ? 'Loading run...' : 'Start Run'}</span>
          </button>
        </div>
      </div>
    </Modal>
  );
};
